import { useEffect, useState } from 'react';

/**
 * In-memory cache of `src → rendered first-page data URL`.
 *
 * Module-scoped so re-mounting the same `FileThumbnail` (e.g. opening
 * the agreement edit modal a second time, or switching tabs) returns
 * synchronously without ever touching the PDF.js worker again. This
 * is what makes the experience feel "smooth" — pdf.js parsing only
 * happens on first sight of each unique URL/blob/data-URI in a session.
 */
const thumbCache = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();

let pdfjsModulePromise: Promise<typeof import('pdfjs-dist')> | null = null;

/**
 * Lazy-load pdf.js and wire its worker. Pulled into a singleton so
 * only the first thumbnail in a session pays the dynamic-import cost;
 * later thumbnails reuse the same module.
 *
 * The worker is loaded from the public unpkg CDN (versioned to match
 * the installed pdfjs-dist) rather than bundled, because resolving
 * `pdfjs-dist/build/pdf.worker.min.mjs` differs across bundlers
 * (Vite, Webpack 5, etc.) and across consumer apps. The CDN path is
 * stable, ~150 KB gzipped, cached aggressively, and requires zero
 * bundler config — the dashboard already needs internet to reach
 * the API anyway, so this introduces no new failure mode.
 */
async function loadPdfjs() {
  if (!pdfjsModulePromise) {
    pdfjsModulePromise = (async () => {
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
      return pdfjs;
    })();
  }
  return pdfjsModulePromise;
}

/**
 * Render the first page of a PDF to an off-screen canvas and return
 * the resulting `data:image/png` URL. Sized for ~300 CSS-pixels of
 * width so the same thumbnail looks crisp across `sm` (32px) up to
 * `xl` (full-row) without re-rendering.
 *
 * The PDF bytes are fetched on the main thread and passed to pdf.js
 * as an `ArrayBuffer`. Two reasons:
 *
 * 1. The pdf.js worker is loaded from a separate origin (unpkg),
 *    and workers can't reliably read `blob:` URLs created on the
 *    main page or follow `data:` URIs, so we materialize the bytes
 *    ourselves.
 * 2. Fetch errors (CORS, 404, network) bubble up here with proper
 *    error objects we can log, instead of being swallowed inside
 *    pdf.js's range-loading machinery.
 */
async function renderPdfThumbnail(src: string): Promise<string | null> {
  const pdfjs = await loadPdfjs();

  const response = await fetch(src);
  if (!response.ok) {
    throw new Error(`PDF fetch failed: ${response.status} ${response.statusText}`);
  }
  const data = await response.arrayBuffer();

  const loadingTask = pdfjs.getDocument({ data });
  try {
    const doc = await loadingTask.promise;
    const page = await doc.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const targetWidth = 300;
    const scale = targetWidth / baseViewport.width;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);

    // pdfjs-dist v6 prefers the `canvas` parameter over the legacy
    // `canvasContext` (the lib derives the 2D context internally and
    // configures it correctly for HiDPI / alpha / text rendering).
    await page.render({ canvas, viewport }).promise;
    return canvas.toDataURL('image/png');
  } finally {
    // pdfjs-dist v6 dropped `PDFDocumentProxy.destroy`; the way to
    // release worker memory is via the loading task. We swallow any
    // teardown error so a failed cleanup doesn't mask the rendered
    // thumbnail (a stale closed tab / abort during navigation can
    // throw here and we don't care — GC eventually picks it up).
    void loadingTask.destroy().catch(() => undefined);
  }
}

/**
 * React hook that produces a cached PNG thumbnail for the first
 * page of a PDF.
 *
 * - `src` may be any URL pdf.js accepts: an HTTPS URL, a `blob:` URL
 *   from `URL.createObjectURL(file)`, or a `data:application/pdf;base64,…`
 *   URI from a freshly-dropped form upload.
 * - Returns `null` while parsing (callers show a fallback icon) and
 *   the data URL once ready.
 * - Repeat calls with the same `src` resolve synchronously from the
 *   shared `thumbCache`, eliminating the perceived lag of inline
 *   `<object data=…>` rendering on modal re-open.
 */
export function usePdfThumbnail(src: string | undefined | null): string | null {
  const [dataUrl, setDataUrl] = useState<string | null>(() =>
    src ? thumbCache.get(src) ?? null : null,
  );

  useEffect(() => {
    if (!src) {
      setDataUrl(null);
      return;
    }

    const cached = thumbCache.get(src);
    if (cached) {
      setDataUrl(cached);
      return;
    }

    let cancelled = false;
    let promise = inflight.get(src);
    if (!promise) {
      promise = renderPdfThumbnail(src).then(
        result => {
          if (result) thumbCache.set(src, result);
          inflight.delete(src);
          return result;
        },
        err => {
          // Surface the failure rather than silently leaving the
          // FileTextIcon fallback in place forever — a misconfigured
          // CDN (no CORS) or stale worker URL is otherwise invisible.
          // eslint-disable-next-line no-console
          console.warn('[usePdfThumbnail] failed to render thumbnail', { src, err });
          inflight.delete(src);
          return null;
        },
      );
      inflight.set(src, promise);
    }
    promise.then(result => {
      if (!cancelled) setDataUrl(result);
    });

    return () => {
      cancelled = true;
    };
  }, [src]);

  return dataUrl;
}
